function normalizeKey(key) {
    return String(key || '').toLowerCase()
}

function parseShortcut(shortcut) {
    const tokens = String(shortcut || '')
        .toLowerCase()
        .split('+')
        .map((token) => token.trim())
        .filter(Boolean)

    const spec = {
        alt: false,
        shift: false,
        ctrl: false,
        meta: false,
        key: '',
    }

    for (const token of tokens) {
        if (token === 'alt') {
            spec.alt = true
            continue
        }
        if (token === 'shift') {
            spec.shift = true
            continue
        }
        if (token === 'ctrl' || token === 'control') {
            spec.ctrl = true
            continue
        }
        if (token === 'meta' || token === 'cmd' || token === 'command') {
            spec.meta = true
            continue
        }
        spec.key = token
    }

    return spec
}

function matchesShortcut(event, spec) {
    if (!spec?.key) return false
    if (event.altKey !== spec.alt) return false
    if (event.shiftKey !== spec.shift) return false
    if (event.ctrlKey !== spec.ctrl) return false
    if (event.metaKey !== spec.meta) return false

    const normalizedEventKey = normalizeKey(event.key)
    if (normalizedEventKey === spec.key) {
        return true
    }

    // Shift+digit can emit symbols like "!" instead of "1"; use KeyboardEvent.code fallback.
    if (/^[0-9]$/.test(spec.key)) {
        return event.code === `Digit${spec.key}` || event.code === `Numpad${spec.key}`
    }

    return false
}

function runAction(event, action, shouldPreventDefault = true) {
    if (shouldPreventDefault) {
        event.preventDefault()
    }
    action?.(event)
}

export function createKeyboardShortcuts({
    target = window,
    capture = true,
    blockCtrlMeta = false,
    bindings = [],
    sequences = [],
} = {}) {
    const parsedBindings = bindings.map((binding) => ({
        ...binding,
        parsedCombo: parseShortcut(binding.combo),
    }))

    const parsedSequences = sequences.map((sequence, index) => ({
        id: sequence.id || `sequence_${index}`,
        parsedFirst: parseShortcut(sequence.first),
        parsedSecond: parseShortcut(sequence.second),
        timeoutMs: sequence.timeoutMs ?? 1500,
        preventDefaultFirst: sequence.preventDefaultFirst !== false,
        preventDefaultSecond: sequence.preventDefaultSecond !== false,
        action: sequence.action,
        when: sequence.when,
    }))

    const armedUntil = {}

    const cleanupExpired = () => {
        const now = Date.now()
        for (const sequence of parsedSequences) {
            if (armedUntil[sequence.id] && armedUntil[sequence.id] <= now) {
                armedUntil[sequence.id] = 0
            }
        }
    }

    const handler = (event) => {
        if (blockCtrlMeta && (event.ctrlKey || event.metaKey)) {
            return
        }

        cleanupExpired()

        for (const binding of parsedBindings) {
            if (!matchesShortcut(event, binding.parsedCombo)) {
                continue
            }
            if (binding.when && !binding.when(event)) {
                return
            }
            runAction(event, binding.action, binding.preventDefault !== false)
            return
        }

        for (const sequence of parsedSequences) {
            if (matchesShortcut(event, sequence.parsedFirst)) {
                if (!sequence.when || sequence.when(event)) {
                    armedUntil[sequence.id] = Date.now() + sequence.timeoutMs
                    if (sequence.preventDefaultFirst) {
                        event.preventDefault()
                    }
                }
                return
            }

            if (armedUntil[sequence.id] && matchesShortcut(event, sequence.parsedSecond)) {
                if (!sequence.when || sequence.when(event)) {
                    armedUntil[sequence.id] = 0
                    runAction(event, sequence.action, sequence.preventDefaultSecond)
                }
                return
            }
        }
    }

    return {
        start() {
            target.addEventListener('keydown', handler, capture)
        },
        stop() {
            target.removeEventListener('keydown', handler, capture)
        },
    }
}
